import React, { Fragment, memo, useCallback, useEffect, useState } from 'react'
import tw from 'twin.macro'
import { Tooltip, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { DeleteOnboardingTemplateModal } from 'src/components/onboarding-template/delete/DeleteOnboardingTemplateModal'
import { OnboardingTemplatesTable } from 'src/components/onboarding-template/onboarding-templates-table/OnboardingTemplatesTable'
import { OnboardingTemplatesTableToolbar } from 'src/components/onboarding-template/onboarding-templates-table/OnboardingTemplatesTableToolbar'
import { Button } from 'src/components/shared/Button'
import {
  AdminDeleteOnboardingTemplate,
  AdminFetchOnboardingTemplateList,
} from 'src/constants/actionTypes'
import { FLOW_EDITOR_FEATURE_FLAGS } from 'src/constants/flowEditorFeatureFlags'
import { ADMIN_ROUTES } from 'src/constants/routes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { OnboardingTemplate } from 'src/interfaces/onboardingTemplate'
import { getOnboardingTemplateDeletingId } from 'src/selectors/onboardingTemplateList'
import { GSIcon } from 'src/components/shared/GSIcon'

const CREATE_DISABLED_REASON =
  'Creating an onboarding template is not supported by the admin API yet.'

const CustomerOnboardingTemplatesPageComponent = () => {
  const { customerId } = useParams<{ customerId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const [searchQuery, setSearchQuery] = useState<string>('')
  // Bumped by the Refresh button — included in the fetch effect's dependency array below so it
  // re-triggers the request.
  const [refreshToken, setRefreshToken] = useState<number>(0)
  const [templatePendingDelete, setTemplatePendingDelete] = useState<OnboardingTemplate | null>(
    null,
  )

  const { isAuthenticated } = useAuthSession()
  // "New template" leads to the create route, which itself requires
  // `collect_data_templates_manage` (AppRoutes). Row "Edit steps" opens the view route and stays.
  // Delete calls the same `collect_data_templates_manage` endpoint family.
  const canManageTemplates = useHasPermission(InternalPermission.CollectDataTemplatesManage)
  const deletingId = useSelector(getOnboardingTemplateDeletingId)

  const isDeletingPendingTemplate =
    templatePendingDelete !== null && deletingId === templatePendingDelete.id

  const handleRefresh = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  // The single fetch effect: the endpoint takes no search or pagination params, so this only
  // reruns on a customer change, an auth change, or a manual refresh — not on the search term.
  useEffect(() => {
    if (!isAuthenticated || !customerId) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchOnboardingTemplateList.request({ partnerId: customerId }))
  }, [asyncDispatchWithErrorToast, isAuthenticated, customerId, refreshToken])

  const handleOpenTemplate = useCallback(
    (template: OnboardingTemplate) => {
      if (!customerId) {
        return
      }

      // The query string carries the editor's `?accessToken=&refreshToken=` entry path, which
      // FLOW_EDITOR.md requires every redirect onto that route to preserve.
      navigate({
        pathname: ADMIN_ROUTES.FLOW_EDITOR_URL.replace(':customerId', customerId).replace(
          ':templateId',
          template.id,
        ),
        search: location.search,
      })
    },
    [customerId, location.search, navigate],
  )

  const handleCreateTemplate = useCallback(() => {
    if (!customerId) {
      return
    }

    navigate({
      pathname: ADMIN_ROUTES.FLOW_EDITOR_NEW_URL.replace(':customerId', customerId),
      search: location.search,
    })
  }, [customerId, location.search, navigate])

  const handleOpenDelete = useCallback((template: OnboardingTemplate) => {
    setTemplatePendingDelete(template)
  }, [])

  // Guarded on the in-flight request so no dismissal path (Cancel, Escape, backdrop) can close
  // the modal mid-delete; that also keeps a second delete from starting.
  const handleCloseDeleteModal = useCallback(() => {
    if (isDeletingPendingTemplate) {
      return
    }

    setTemplatePendingDelete(null)
  }, [isDeletingPendingTemplate])

  const handleConfirmDelete = useCallback(async () => {
    if (!templatePendingDelete) {
      return
    }

    // A rejected delete (409 while contractors are assigned) resolves to null after the error
    // toast has shown the backend's message; the modal stays open on the template picked.
    const deletedId = await asyncDispatchWithErrorToast<string>(
      AdminDeleteOnboardingTemplate.request({ collectDataTemplateId: templatePendingDelete.id }),
    )

    if (!deletedId) {
      return
    }

    snackbar?.enqueueSnackbar('Template deleted', { variant: 'success' })
    setTemplatePendingDelete(null)
    handleRefresh()
  }, [templatePendingDelete, asyncDispatchWithErrorToast, snackbar, handleRefresh])

  return (
    <Fragment>
      <div data-testid="admin-customer-onboarding-templates-page" css={tw`min-w-0 flex-1 p-6`}>
        <div css={tw`mb-5 flex flex-row items-start justify-between gap-4`}>
          <div>
            <Typography variant="h2">Onboarding Templates</Typography>
          </div>
          {canManageTemplates && FLOW_EDITOR_FEATURE_FLAGS.allowCreateTemplate ? (
            <Button
              size="small"
              color="dark"
              leftIcon={<GSIcon iconName="gs_plus_medium" size={20} css={tw`text-shades-0`} />}
              onClick={handleCreateTemplate}
              data-testid="onboarding-templates-list-add-button"
            >
              New template
            </Button>
          ) : null}
          {canManageTemplates && !FLOW_EDITOR_FEATURE_FLAGS.allowCreateTemplate ? (
            <Tooltip title={CREATE_DISABLED_REASON}>
              {/* A disabled MUI button swallows pointer events; the span keeps the tooltip alive. */}
              <span>
                <Button
                  size="small"
                  color="dark"
                  disabled
                  leftIcon={<GSIcon iconName="gs_plus_medium" size={20} css={tw`text-shades-0`} />}
                  data-testid="onboarding-templates-list-add-button"
                >
                  New template
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </div>
        <OnboardingTemplatesTableToolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onRefresh={handleRefresh}
        />
        <OnboardingTemplatesTable
          searchQuery={searchQuery}
          canDeleteTemplates={canManageTemplates}
          onOpenTemplate={handleOpenTemplate}
          onDeleteTemplate={handleOpenDelete}
        />
      </div>
      <DeleteOnboardingTemplateModal
        template={templatePendingDelete}
        isDeleting={isDeletingPendingTemplate}
        onConfirm={handleConfirmDelete}
        onClose={handleCloseDeleteModal}
      />
    </Fragment>
  )
}

export const CustomerOnboardingTemplatesPage = memo(CustomerOnboardingTemplatesPageComponent)
